import request from '@/utils/request'

export interface Device {
  id: number
  serialNumber: string
  productType: string
  purchaseType: string
  currentUserId?: string
  lifecycleStatus: string
  purchaseDate?: Date
  activationDate?: Date
  warrantyEndDate?: Date
  warrantyPeriodId?: number
  zohoOrderId?: string
  notes?: string
  metadataJson?: any
  createdAt: Date
  updatedAt: Date
}

// ============ Device CRUD ============

export const searchDevices = (params: {
  serialNumber?: string
  productType?: string
  userId?: string
  lifecycleStatus?: string
  limit?: number
  offset?: number
}) =>
  request({
    url: '/devices/search',
    method: 'get',
    params
  })

export const getDevice = (serialNumber: string) =>
  request({
    url: `/devices/${serialNumber}`,
    method: 'get'
  })

export const registerDevice = (data: {
  serialNumber: string
  productType: string
  purchaseType?: string
  purchaseDate?: string
  warrantyPeriodId?: number
  warrantyEndDate?: string
  zohoOrderId?: string
  notes?: string
  features?: Array<{
    featureKey: string
    durationType: string
    durationValue?: number
  }>
}) =>
  request({
    url: '/devices/register',
    method: 'post',
    data
  })

export const getDeviceHistory = (serialNumber: string) =>
  request({
    url: `/devices/${serialNumber}/history`,
    method: 'get'
  })

// ============ Device Connection ============

export const connectDevice = (serialNumber: string, userId: string) =>
  request({
    url: `/devices/${serialNumber}/connect`,
    method: 'post',
    data: { userId }
  })

export const disconnectDevice = (serialNumber: string, userId: string, reason?: string) =>
  request({
    url: `/devices/${serialNumber}/disconnect`,
    method: 'post',
    data: { userId, reason }
  })

// Admin force disconnect - bypasses user check
export const forceDisconnectDevice = (serialNumber: string, reason?: string) =>
  request({
    url: `/devices/${serialNumber}/disconnect`,
    method: 'post',
    data: { force: true, reason }
  })

// ============ Lifecycle Operations ============

export const updateLifecycle = (serialNumber: string, data: {
  status: string
  reason?: string
  adminId?: string
}) =>
  request({
    url: `/devices/${serialNumber}/lifecycle`,
    method: 'put',
    data
  })

// ============ Repair Operations ============

export const startRepair = (serialNumber: string, data: {
  reason: string
  adminId?: string
}) =>
  request({
    url: `/devices/${serialNumber}/repair/start`,
    method: 'post',
    data
  })

export const completeRepair = (serialNumber: string, data: {
  repairDays: number
  notes?: string
  adminId?: string
}) =>
  request({
    url: `/devices/${serialNumber}/repair/complete`,
    method: 'post',
    data
  })

// ============ Refund Operations ============

export const processRefund = (serialNumber: string, data: {
  reason: string
  adminId?: string
}) =>
  request({
    url: `/devices/${serialNumber}/refund`,
    method: 'post',
    data
  })

// ============ Replace Operations ============

export const replaceDevice = (serialNumber: string, data: {
  newSerialNumber: string
  newProductType: string
  reason?: string
  adminId?: string
}) =>
  request({
    url: `/devices/${serialNumber}/replace`,
    method: 'post',
    data
  })

export const replaceWithFeatureTransfer = (serialNumber: string, data: {
  newSerialNumber: string
  newProductType: string
  reason?: string
  adminId?: string
}) =>
  request({
    url: `/devices/${serialNumber}/replace-with-transfer`,
    method: 'post',
    data
  })

// ============ Bulk Import with SKU ============

export interface BulkDeviceWithSku {
  serialNumber: string
  skuCode: string
  purchaseDate?: string
  productType?: string
  purchaseType?: string
  notes?: string
  // Purchaser information
  purchaserFirstName?: string
  purchaserLastName?: string
  purchaserEmail?: string
  purchaserPhone?: string
  purchaserCountryCode?: string
  purchaserCountry?: string
  purchaserAddress?: string
}

export interface BulkRegisterWithSkuResponse {
  success: boolean
  requiresSkuCreation?: boolean
  data: {
    missingSkus?: string[]
    existingSkus?: string[]
    totalDevices?: number
    devicesWithMissingSku?: number
    successCount?: number
    failedCount?: number
    success?: Array<{
      serialNumber: string
      skuCode: string
      productType: string
    }>
    failed?: Array<{
      device: BulkDeviceWithSku
      error: string
    }>
  }
}

export const bulkRegisterWithSku = (devices: BulkDeviceWithSku[], validateOnly = false) =>
  request({
    url: '/devices/bulk-register-with-sku',
    method: 'post',
    data: { devices, validateOnly },
    timeout: 300000 // 5 minutes for bulk operations
  })

export const validateSkuCodes = (skuCodes: string[]) =>
  request({
    url: '/devices/validate-skus',
    method: 'post',
    data: { skuCodes },
    timeout: 60000 // 1 minute for validation
  })

// ============ Zoho Sync Missing Features ============

export const syncMissingFeaturesScan = (zohoPage = 1) =>
  request({
    url: '/zoho/sync-missing-features',
    method: 'post',
    params: { dryRun: 'true' },
    data: { limit: 200, zohoPage },
    timeout: 300000
  })

export const syncMissingFeaturesApply = (serialNumbers: string[]) =>
  request({
    url: '/zoho/sync-missing-features',
    method: 'post',
    params: { dryRun: 'false' },
    data: { limit: 200, serialNumbers },
    timeout: 300000
  })

export const bulkRegisterDevices = (devices: Array<{
  serialNumber: string
  productType: string
  purchaseType?: string
  skuCode?: string
  purchaseDate?: string
  notes?: string
}>) =>
  request({
    url: '/devices/bulk-register',
    method: 'post',
    data: { devices },
    timeout: 300000 // 5 minutes for bulk operations
  })
